import { Node as ProsemirrorNode } from 'prosemirror-model';
import { NodeView, EditorView } from 'prosemirror-view';
import { NodeSelection } from 'prosemirror-state';
import { createRoot, Root } from 'react-dom/client';
import PdfViewer from '../../../../../components/PdfViewer';
import { NodeExtension, CommandFunction, ApplySchemaAttributes, NodeSpecOverride } from '@remirror/core';
import { MediaUploadingPlaceholder } from '../MediaUploadedPlaceholder';
import { FileText } from 'lucide-react';

import styled from 'styled-components';
import { AttachedFile } from '../../../../../types/attached-file';
import { CustomPdfExtensionStyles } from './styles';

export const PdfWrapper = styled.div`
  background-color: #f7f7f7;
  border-radius: 10px;
  padding: 14px 16px;
  display: flex;
  align-items: center;
  gap: 12px;
  color: #333;
  font-family: 'Inter', sans-serif;
  font-size: 14px;
  max-width: 100%;
  border: 1px solid #ddd;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.05);
`;

export const PdfIcon = styled.div`
  font-size: 20px;
  color: #d32f2f; /* rojo PDF */
  display: flex;
  align-items: center;
  justify-content: center;
`;

export const PdfInfo = styled.div`
  flex: 1;
  overflow: hidden;
`;

export const PdfTitle = styled.div`
  font-weight: 600;
  color: #222;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

export const PdfMeta = styled.div`
  font-size: 12px;
  color: #777;
  margin-top: 2px;
`;


export class CustomPdfNodeView implements NodeView {
  dom: HTMLElement;
  reactContainer: HTMLElement;
  root: Root | null = null;

  private startX = 0;
  private startY = 0;
  private startWidth = 0;
  private startHeight = 0;
  private originalRatio = 8.5 / 11; // A4 portrait ratio

  constructor(
    private node: ProsemirrorNode,
    private view: EditorView,
    private getPos: () => number,
    private mediaFilesMap?: Record<number, AttachedFile>,
  ) {
    this.dom = document.createElement('div');
    this.dom.className = 'remirror-pdf-wrapper';
    this.dom.setAttribute('data-node-type', 'pdf');

    this.dom.style.width = '100%';
    this.originalRatio = 8.5 / 11;

    this.reactContainer = document.createElement('div');
    this.reactContainer.style.width = '100%';
    this.reactContainer.style.height = '100%';
    this.dom.appendChild(this.reactContainer);

    this.renderContent();
    this.handleSelection();

    if (this.view.state.selection instanceof NodeSelection &&
      this.view.state.selection.from === this.getPos()) {
      this.dom.setAttribute('data-selected', 'true');
    }
  }

  update(node: ProsemirrorNode) {
    if (node.type !== this.node.type) return false;
    this.node = node;
    this.renderContent();
    this.handleSelection();
    return true;
  }

  selectNode() {
    this.dom.setAttribute('data-selected', 'true');
  }

  deselectNode() {
    this.dom.removeAttribute('data-selected');
  }

  destroy() {
    this.root?.unmount();
  }

  renderContent() {
    const {
      src,
      filename,
      "data-uploading": uploading,
      "data-progress": progress,
    } = this.node.attrs;

    const isUploading = uploading === true || uploading === "true" || uploading === "1";

    if (this.root) this.root.unmount();
    this.reactContainer.innerHTML = "";

    // Eliminar cualquier badge de progreso existente
    const existingBadge = this.dom.querySelector(".progress-badge");
    if (existingBadge) {
      existingBadge.remove();
    }

    this.root = createRoot(this.reactContainer);

    if (isUploading) {
      this.root.render(
        <MediaUploadingPlaceholder
          type="pdf"
          filename={filename}
          progress={progress}
        />
      );
    } else {
      this.root.render(
        <PdfWrapper>
          <PdfIcon><FileText size={20} /></PdfIcon>
          <PdfInfo>
            <PdfTitle>{filename || "Archivo PDF"}</PdfTitle>
            <PdfMeta>En la vista previa del articulo podrás ver el PDF cargado</PdfMeta>
          </PdfInfo>
        </PdfWrapper>
      );
    }

  }

  handleSelection = () => {
    this.dom.addEventListener("click", (e) => {
      e.preventDefault();
      e.stopPropagation();
      if (this.dom.classList.contains("resizing")) return;

      // Default behavior: select the entire node
      const pos = this.getPos();
      const nodeSelection = NodeSelection.create(this.view.state.doc, pos);
      this.view.dispatch(this.view.state.tr.setSelection(nodeSelection));
    });
  };
}

export class PreviewPdfNodeView implements NodeView {
  dom: HTMLElement;
  reactContainer: HTMLElement;
  root: Root | null = null;

  constructor(
    private node: ProsemirrorNode,
    private view: EditorView,
    private mediaFilesMap?: Record<number, AttachedFile>,
    private isOwner: boolean = false,
    private blockFilesDownload: boolean = true
  ) {
    this.dom = document.createElement("div");
    this.dom.className = "remirror-pdf-wrapper";
    this.dom.draggable = false;
    this.dom.style.width = `100%`;
    // this.dom.style.height = `${height}px`;
    // const width = node.attrs.width ?? 600;
    // const height = node.attrs.height ?? 780;
    // this.dom.style.width = `${width}px`;
    // this.dom.style.height = `${height}px`;

    this.reactContainer = document.createElement("div");
    this.reactContainer.style.width = "100%";
    // this.reactContainer.style.height = "100%";
    this.dom.appendChild(this.reactContainer);

    this.renderContent();
    this.applyDownloadBlock();
    this.updateBlockStatus();
  }

  update(node: ProsemirrorNode) {
    if (node.type !== this.node.type) return false;
    this.node = node;
    this.renderContent();
    return true;
  }

  renderContent() {
    const { src, filename } = this.node.attrs;

    if (this.root) this.root.unmount();
    this.reactContainer.innerHTML = '';
    this.root = createRoot(this.reactContainer);

    const finalSrc = this.mediaFilesMap?.[this.node.attrs.media_file_id]?.signed_url || src;

    // Aplicar bloqueo de descargas usando los parámetros de la extensión
    const shouldBlock = this.blockFilesDownload && !this.isOwner;




    this.root.render(
      <PdfViewer
        fileUrl={finalSrc}
        fileName={filename}
        blockDownloadFiles={shouldBlock}
        isOwner={this.isOwner}
      />
    );
  }

  private applyDownloadBlock() {
    const shouldBlock = this.blockFilesDownload && !this.isOwner;

    // Agregar event listeners para prevenir descargas
    this.dom.addEventListener('contextmenu', (e) => {
      if (shouldBlock) {
        e.preventDefault();
        e.stopPropagation();
        return false;
      }
    });

    this.dom.addEventListener('dragstart', (e) => {
      if (shouldBlock) {
        e.preventDefault();
        e.stopPropagation();
        return false;
      }
    });

    this.dom.addEventListener('selectstart', (e) => {
      if (shouldBlock) {
        e.preventDefault();
        e.stopPropagation();
        return false;
      }
    });

    this.dom.addEventListener('copy', (e) => {
      if (shouldBlock) {
        e.preventDefault();
        e.stopPropagation();
        return false;
      }
    });

    this.dom.addEventListener('cut', (e) => {
      if (shouldBlock) {
        e.preventDefault();
        e.stopPropagation();
        return false;
      }
    });

    // Aplicar clases CSS para bloqueo visual
    if (shouldBlock) {
      this.dom.classList.add('download-blocked');
      this.reactContainer.classList.add('download-blocked');
    } else {
      this.dom.classList.remove('download-blocked');
      this.reactContainer.classList.remove('download-blocked');
    }
  }

  private updateBlockStatus() {
    const shouldBlock = this.blockFilesDownload && !this.isOwner;

    if (shouldBlock) {
      this.dom.classList.add('download-blocked');
      this.reactContainer.classList.add('download-blocked');
    } else {
      this.dom.classList.remove('download-blocked');
      this.reactContainer.classList.remove('download-blocked');
    }
  }

  private getBlockStatus() {
    const shouldBlock = this.blockFilesDownload && !this.isOwner;

    return {
      blockFilesDownload: this.blockFilesDownload,
      isOwner: this.isOwner,
      finalBlockDownload: shouldBlock,
      hasBlockClasses: this.dom.classList.contains('download-blocked'),
      containerHasBlockClasses: this.reactContainer.classList.contains('download-blocked')
    };
  }

  destroy() {
    this.root?.unmount();
  }
}


export class CustomPdfExtension extends NodeExtension {
  get name() {
    return 'customPdf'
  }

  constructor(
    private mediaFilesMap?: Record<number, AttachedFile>,
    private isOwner: boolean = false,
    private blockFilesDownload: boolean = true
  ) {
    super();
  }


  override createNodeSpec(extra: ApplySchemaAttributes, override: NodeSpecOverride) {
    return {
      inline: false,
      group: 'block',
      atom: true,
      selectable: true,
      draggable: true,
      attrs: {
        id: { default: null },
        src: { default: '' },
        filename: { default: null },
        blockFilesDownload: { default: true },
        "data-uploading": { default: null },
        "data-progress": { default: null },
        media_file_id: { default: null },
        ...extra.defaults(),
      },
      parseDOM: [{
        tag: 'div[data-pdf-src]',
        getAttrs: (dom: HTMLElement) => ({
          src: dom.getAttribute('data-pdf-src') || '',
          filename: dom.getAttribute('data-pdf-filename') || '',
          blockFilesDownload: dom.getAttribute('data-block-files-download') === 'true',
          "data-uploading": dom.getAttribute('data-uploading') || null,
          "data-progress": dom.getAttribute('data-progress') || null,
          media_file_id: dom.getAttribute('media_file_id') || null,
          ...extra.parse(dom),
        }),
      }],
      toDOM: (node) => {
        const { src, filename, blockFilesDownload,
          "data-uploading": uploading,
          "data-progress": progress,
        } = node.attrs;

        const style = `width: 100%;`;

        return [
          'div',
          {
            class: 'remirror-pdf-wrapper',
            'data-pdf-src': src,
            'data-pdf-filename': filename,
            'data-block-files-download': blockFilesDownload,
            'data-uploading': uploading ?? undefined,
            'data-progress': progress ?? undefined,
            style,
            ...extra.dom(node),
          },
        ] as any;
      },
    };
  }

  override createCommands() {
    return {
      insertPdf: (options: {
        id?: string;
        src: string;
        filename?: string;
        blockFilesDownload?: boolean;
        uploading?: boolean;
        progress?: number | null;
      }): CommandFunction => {
        return ({ tr, dispatch }) => {
          const node = this.type.create({
            src: options.src,
            filename: options.filename ?? null,
            "data-uploading": options.uploading ?? false,
            "data-progress": options.progress ?? null,
            blockFilesDownload: options.blockFilesDownload ?? true,
            id: options.id ?? null,
          });
          dispatch?.(tr.replaceSelectionWith(node).scrollIntoView());
          return true;
        };
      },
    };
  }

  override createNodeViews() {
    return (node, view, getPos) => {
      if (!view.editable) {
        return new PreviewPdfNodeView(node, view, this.mediaFilesMap, this.isOwner, this.blockFilesDownload);
      }
      return new CustomPdfNodeView(node, view, getPos!, this.mediaFilesMap);
    };
  }
}
